import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Calculator, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function calcLine(ln, regime, sameState) {
  const qty = parseFloat(ln.qty) || 0;
  const up = parseFloat(ln.unit_price) || 0;
  const disc = parseFloat(ln.discount_pct) || 0;
  const rate = parseFloat(ln.tax_rate) || 0;
  const gross = qty * up;
  const discount = gross * disc / 100;
  const taxable = gross - discount;
  let cgst = 0, sgst = 0, igst = 0, vat = 0, sales_tax = 0;
  if (regime === "IN_GST") {
    if (sameState) { cgst = taxable * (rate/2) / 100; sgst = taxable * (rate/2) / 100; }
    else { igst = taxable * rate / 100; }
  } else if (["AE_VAT", "EU_VAT", "UK_VAT"].includes(regime)) {
    vat = taxable * rate / 100;
  } else if (regime === "US_SALES_TAX") {
    sales_tax = taxable * rate / 100;
  }
  const tax_total = cgst + sgst + igst + vat + sales_tax;
  return { taxable, cgst, sgst, igst, vat, sales_tax, tax_total, total: taxable + tax_total };
}

export default function InvoiceCreate({ direction: propDirection = "sale" }) {
  const { active } = useCompany();
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fx, setFx] = useState(1);
  const [fxLoading, setFxLoading] = useState(false);
  const [direction, setDirection] = useState(propDirection);

  const [form, setForm] = useState({
    party_id: "",
    number: "",
    payment_link: "",
    invoice_type: "tax_invoice",
    invoice_date: todayIso(),
    due_date: "",
    currency: active?.base_currency || "INR",
    exchange_rate: 1,
    place_of_supply: "",
    notes: "",
    reference: "",
    lines: [{ item_id: "", name: "", hsn: "", unit: "pcs", qty: 1, unit_price: 0, discount_pct: 0, tax_rate: 18 }],
  });

  useEffect(() => {
    if (!active) return;
    if (!isEdit) setForm((f) => ({ ...f, currency: active.base_currency }));
    const partyType = direction === "purchase" ? "vendor" : "customer";
    Promise.all([
      api.get(`/parties?type=${partyType}`),
      api.get("/items"),
    ]).then(([p, i]) => {
      setParties(p.data.parties || []);
      setItems(i.data.items || []);
    });
  }, [active, direction, isEdit]);

  // Load invoice in edit mode
  useEffect(() => {
    if (!isEdit || !active) return;
    api.get(`/invoices/${editId}`).then(({ data }) => {
      setDirection(data.direction || "sale");
      setForm({
        party_id: data.party_id || "",
        number: data.number || "",
        payment_link: data.payment_link || "",
        invoice_type: data.invoice_type || "tax_invoice",
        invoice_date: data.invoice_date || todayIso(),
        due_date: data.due_date || "",
        currency: data.currency || active.base_currency,
        exchange_rate: data.exchange_rate || 1,
        place_of_supply: data.place_of_supply || "",
        notes: data.notes || "",
        reference: data.reference || "",
        lines: (data.lines || []).map((ln) => ({
          item_id: ln.item_id || "", name: ln.name || "", hsn: ln.hsn || "",
          unit: ln.unit || "pcs", qty: ln.qty || 1, unit_price: ln.unit_price || 0,
          discount_pct: ln.discount_pct || 0, tax_rate: ln.tax_rate || 0,
        })),
      });
    }).catch(() => {
      toast.error("Failed to load invoice");
      navigate("/app/invoices");
    });
    // eslint-disable-next-line
  }, [isEdit, editId, active]);

  const selectedParty = parties.find((p) => p.id === form.party_id);
  const regime = form.invoice_type === "bill_of_supply" ? "NONE" : (active?.tax_regime || "NONE");
  const sameState = active?.state_code && (
    form.place_of_supply === active.state_code ||
    selectedParty?.state_code === active.state_code
  );

  const computed = useMemo(() => {
    const enriched = form.lines.map((ln) => ({ ...ln, ...calcLine(ln, regime, sameState) }));
    const totals = enriched.reduce((acc, l) => ({
      subtotal: acc.subtotal + l.taxable,
      cgst: acc.cgst + l.cgst,
      sgst: acc.sgst + l.sgst,
      igst: acc.igst + l.igst,
      vat: acc.vat + l.vat,
      sales_tax: acc.sales_tax + l.sales_tax,
      tax_total: acc.tax_total + l.tax_total,
      grand_total: acc.grand_total + l.total,
    }), { subtotal: 0, cgst: 0, sgst: 0, igst: 0, vat: 0, sales_tax: 0, tax_total: 0, grand_total: 0 });
    return { enriched, totals };
  }, [form.lines, regime, sameState]);

  const addLine = () => setForm((f) => ({
    ...f, lines: [...f.lines, { item_id: "", name: "", hsn: "", unit: "pcs", qty: 1, unit_price: 0, discount_pct: 0, tax_rate: 18 }],
  }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({
    ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l),
  }));

  const onSelectItem = (i, itemId) => {
    if (!itemId) { updateLine(i, { item_id: "" }); return; }
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    updateLine(i, {
      item_id: it.id, name: it.name, hsn: it.hsn || "",
      unit: it.unit, unit_price: it.sale_price, tax_rate: it.tax_rate,
    });
  };

  const fetchFx = async () => {
    if (!form.currency || form.currency === active?.base_currency) { setFx(1); return; }
    setFxLoading(true);
    try {
      const { data } = await api.get(`/exchange-rates/fetch?from=${form.currency}&to=${active.base_currency}`);
      setFx(data.rate);
      setForm((f) => ({ ...f, exchange_rate: data.rate }));
      toast.success(`Rate: 1 ${form.currency} = ${data.rate.toFixed(4)} ${active.base_currency}`);
    } catch {
      toast.error("Failed to fetch FX rate");
    } finally { setFxLoading(false); }
  };

  const submit = async (status) => {
    if (!form.party_id) { toast.error(direction === "purchase" ? "Select a vendor" : "Select a customer"); return; }
    if (!form.lines.length || !form.lines.some((l) => l.name)) {
      toast.error("Add at least one line item"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, status, direction };
      if (isEdit) {
        const { data } = await api.put(`/invoices/${editId}`, payload);
        toast.success(`Invoice ${data.number} updated`);
        navigate(`/app/invoices/${data.id}`);
      } else {
        const { data } = await api.post("/invoices", payload);
        toast.success(`Invoice ${data.number} created`);
        navigate(`/app/invoices/${data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save invoice");
    } finally { setSaving(false); }
  };

  const cur = form.currency;

  return (
    <div className="space-y-4" data-testid="invoice-create-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">{isEdit ? "Edit" : "New"} {direction === "purchase" ? "Purchase Bill" : "Invoice"}</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">
            {isEdit ? "Edit Invoice" : (direction === "purchase" ? "Record Purchase Bill" : "Create Invoice")}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="save-draft-btn" onClick={() => submit("draft")} disabled={saving} className="btn-secondary flex-1 sm:flex-none justify-center">
            <Save size={14}/> Save Draft
          </button>
          <button data-testid="save-send-btn" onClick={() => submit("sent")} disabled={saving} className="btn-primary flex-1 sm:flex-none justify-center">
            <Save size={14}/> {isEdit ? "Save Changes" : (direction === "purchase" ? "Save Bill" : "Save & Send")}
          </button>
        </div>
      </div>

      {/* Header row */}
      <div className="card-surface p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="label-field">Tax Treatment</label>
          <select
            data-testid="inv-type" className="input-clean" value={form.invoice_type}
            onChange={(e) => setForm({ ...form, invoice_type: e.target.value })}
          >
            <option value="tax_invoice">Taxable (Tax Invoice)</option>
            <option value="bill_of_supply">Non-Taxable (Bill of Supply / Export)</option>
          </select>
          <div className="text-[11px] text-[#64748B] mt-1">
            {form.invoice_type === "tax_invoice"
              ? "GST / VAT will be computed per your country profile."
              : "Zero-rated — no tax added (export or exempt supply)."}
          </div>
        </div>
        <div>
          <label className="label-field">{direction === "purchase" ? "Vendor *" : "Customer *"}</label>
          <select
            data-testid="inv-party" className="input-clean" value={form.party_id}
            onChange={(e) => setForm({ ...form, party_id: e.target.value })}
          >
            <option value="">Select {direction === "purchase" ? "vendor" : "customer"}…</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.gstin ? ` · ${p.gstin}` : ""}</option>)}
          </select>
          {parties.length === 0 && (
            <button onClick={() => navigate(`/app/parties?type=${direction === "purchase" ? "vendor" : "customer"}`)} className="text-xs text-[#1E3A8A] mt-1 hover:underline">
              + Add first {direction === "purchase" ? "vendor" : "customer"}
            </button>
          )}
        </div>
        <div>
          <label className="label-field">Invoice Date *</label>
          <input data-testid="inv-date" type="date" className="input-clean" value={form.invoice_date}
            onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}/>
        </div>
        <div>
          <label className="label-field">Invoice # <span className="text-[#94A3B8] font-normal">(optional override)</span></label>
          <input
            data-testid="inv-number"
            className="input-clean font-mono-data"
            value={form.number}
            placeholder="Leave blank to auto-generate"
            onChange={(e) => setForm({ ...form, number: e.target.value })}
          />
          <p className="text-[10px] text-[#94A3B8] mt-0.5">
            Type your own number (e.g. <code className="font-mono-data">INV/2026-27/0001</code>) or leave blank to use the next automatic sequence.
          </p>
        </div>
        <div>
          <label className="label-field">Pay-Now Link <span className="text-[#94A3B8] font-normal">(optional)</span></label>
          <input
            data-testid="inv-payment-link"
            type="url"
            className="input-clean"
            value={form.payment_link}
            placeholder="https://…  (Stripe / Razorpay / any URL)"
            onChange={(e) => setForm({ ...form, payment_link: e.target.value })}
          />
          <p className="text-[10px] text-[#94A3B8] mt-0.5">
            Appears inside the auto-sent WhatsApp &amp; email so customers can pay in one click.
          </p>
        </div>
        <div>
          <label className="label-field">Due Date</label>
          <input data-testid="inv-due" type="date" className="input-clean" value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}/>
        </div>
        <div>
          <label className="label-field">Billing Currency</label>
          <select
            data-testid="inv-currency" className="input-clean font-mono-data"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          >
            <optgroup label="Your base">
              <option value={active?.base_currency || "INR"}>{active?.base_currency || "INR"} — base</option>
            </optgroup>
            <optgroup label="Major">
              {["USD","EUR","GBP","AED","SGD","AUD","CAD","JPY","CNY","CHF","HKD"].filter((c) => c !== active?.base_currency).map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
            <optgroup label="Regional">
              {["INR","TZS","ZMW","KES","UGX","NGN","ZAR","EGP","GHS","RWF","MZN","BWP","IDR","PHP","VND","THB","MYR","LKR","BDT","NPR","PKR"].filter((c) => c !== active?.base_currency).map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="label-field">Exchange Rate</label>
          <div className="flex gap-2">
            <input data-testid="inv-fx" type="number" step="0.0001" className="input-clean font-mono-data"
              value={form.exchange_rate}
              onChange={(e) => setForm({ ...form, exchange_rate: parseFloat(e.target.value) || 1 })}/>
            <button data-testid="fetch-fx-btn" onClick={fetchFx} disabled={fxLoading} className="btn-ghost">
              <RefreshCw size={14} className={fxLoading ? "animate-spin" : ""}/>
            </button>
          </div>
        </div>
        {regime === "IN_GST" && (
          <div className="md:col-span-2">
            <label className="label-field">Place of Supply (State Code)</label>
            <input data-testid="inv-pos" className="input-clean font-mono-data uppercase"
              value={form.place_of_supply} maxLength={2} placeholder="e.g. MH"
              onChange={(e) => setForm({ ...form, place_of_supply: e.target.value.toUpperCase() })}/>
            <div className="text-[11px] text-[#64748B] mt-1">
              {sameState ? "Intra-state → CGST + SGST" : "Inter-state → IGST"}
            </div>
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Line Items</h3>
          <button onClick={addLine} data-testid="add-line-btn" className="btn-ghost text-xs">
            <Plus size={12}/> Add Line
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="table-clean" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Item</th>
                <th style={{ width: 90 }}>HSN</th>
                <th style={{ width: 70 }}>Qty</th>
                <th style={{ width: 100 }}>Price</th>
                <th style={{ width: 70 }}>Disc %</th>
                <th style={{ width: 70 }}>Tax %</th>
                <th className="text-right">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((ln, i) => (
                <tr key={i} data-testid={`inv-line-${i}`}>
                  <td>
                    {items.length > 0 && (
                      <select className="input-clean text-xs mb-1" value={ln.item_id}
                        data-testid={`line-item-select-${i}`}
                        onChange={(e) => onSelectItem(i, e.target.value)}>
                        <option value="">— pick item —</option>
                        {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </select>
                    )}
                    <input className="input-clean text-sm" placeholder="Item name"
                      data-testid={`line-name-${i}`}
                      value={ln.name} onChange={(e) => updateLine(i, { name: e.target.value })}/>
                  </td>
                  <td>
                    <input className="input-clean text-sm font-mono-data" placeholder="HSN"
                      value={ln.hsn} onChange={(e) => updateLine(i, { hsn: e.target.value })}/>
                  </td>
                  <td>
                    <input type="number" step="0.01" className="input-clean text-sm font-mono-data" value={ln.qty}
                      data-testid={`line-qty-${i}`}
                      onChange={(e) => updateLine(i, { qty: e.target.value })}/>
                  </td>
                  <td>
                    <input type="number" step="0.01" className="input-clean text-sm font-mono-data" value={ln.unit_price}
                      data-testid={`line-price-${i}`}
                      onChange={(e) => updateLine(i, { unit_price: e.target.value })}/>
                  </td>
                  <td>
                    <input type="number" step="0.01" className="input-clean text-sm font-mono-data" value={ln.discount_pct}
                      onChange={(e) => updateLine(i, { discount_pct: e.target.value })}/>
                  </td>
                  <td>
                    <input type="number" step="0.01" className="input-clean text-sm font-mono-data" value={ln.tax_rate}
                      data-testid={`line-tax-${i}`}
                      onChange={(e) => updateLine(i, { tax_rate: e.target.value })}/>
                  </td>
                  <td className="text-right font-mono-data font-semibold">
                    {fmtMoney(computed.enriched[i]?.total || 0, cur)}
                  </td>
                  <td>
                    {form.lines.length > 1 && (
                      <button onClick={() => removeLine(i)} data-testid={`line-remove-${i}`} className="text-[#DC2626]">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-surface p-4">
          <label className="label-field">Notes / Terms</label>
          <textarea
            data-testid="inv-notes" className="input-clean" rows={5} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Payment terms, delivery details, etc."
          />
        </div>
        <div className="card-surface p-4" data-testid="inv-totals-card">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Calculator size={16}/> Summary
          </h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Subtotal</span><span className="font-mono-data">{fmtMoney(computed.totals.subtotal, cur)}</span></div>
            {computed.totals.cgst > 0 && <div className="flex justify-between"><span className="text-[#64748B]">CGST</span><span className="font-mono-data">{fmtMoney(computed.totals.cgst, cur)}</span></div>}
            {computed.totals.sgst > 0 && <div className="flex justify-between"><span className="text-[#64748B]">SGST</span><span className="font-mono-data">{fmtMoney(computed.totals.sgst, cur)}</span></div>}
            {computed.totals.igst > 0 && <div className="flex justify-between"><span className="text-[#64748B]">IGST</span><span className="font-mono-data">{fmtMoney(computed.totals.igst, cur)}</span></div>}
            {computed.totals.vat > 0 && <div className="flex justify-between"><span className="text-[#64748B]">VAT</span><span className="font-mono-data">{fmtMoney(computed.totals.vat, cur)}</span></div>}
            {computed.totals.sales_tax > 0 && <div className="flex justify-between"><span className="text-[#64748B]">Sales Tax</span><span className="font-mono-data">{fmtMoney(computed.totals.sales_tax, cur)}</span></div>}
            <div className="border-t border-[#E2E8F0] pt-2 flex justify-between text-base font-bold">
              <span>Grand Total</span>
              <span className="font-mono-data" data-testid="inv-grand-total">{fmtMoney(computed.totals.grand_total, cur)}</span>
            </div>
            {form.currency !== active?.base_currency && (
              <div className="text-xs text-[#64748B] pt-1">
                ≈ {fmtMoney(computed.totals.grand_total * (parseFloat(form.exchange_rate) || 1), active?.base_currency)} in base currency
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
